"use client";

import { ChevronLeft } from "lucide-react";
import { useRouter } from "next/navigation";
import { useRef } from "react";
import { Button } from "@/components/ui/button";
import Link from "@/components/ui/link";
import { useSwipeBackTarget } from "@/hooks/use-swipe-back-target";

type PaymentPageHeaderProps = {
  title: string;
  backHref: string;
  backLabel?: string;
  /** Disables the link and routes the tap through `onBack` instead. */
  disabled?: boolean;
  /**
   * Take over the back navigation even when enabled — used when leaving has a
   * side effect (cancelling an unpaid hold) that needs a confirmation first.
   */
  interceptBack?: boolean;
  onBack?: () => void;
};

/**
 * The fixed top bar every checkout screen shares. Renders its own 56px spacer
 * so callers drop it straight into the PaymentShell content flow.
 */
export function PaymentPageHeader({
  title,
  backHref,
  backLabel = "Go back",
  disabled = false,
  interceptBack = false,
  onBack,
}: PaymentPageHeaderProps) {
  const router = useRouter();
  const headerRef = useRef<HTMLElement>(null);
  // The edge swipe does what the arrow does: nothing while it is disabled,
  // the confirmation when leaving has a side effect (the page springs back so
  // the question appears over it), otherwise the same `replace` as the link.
  useSwipeBackTarget(
    headerRef,
    () => {
      if (interceptBack) onBack?.();
      else router.replace(backHref);
    },
    {
      enabled: !disabled,
      mode: interceptBack ? "confirm" : "navigate",
      previewHref: backHref,
    },
  );

  return (
    <>
      <header
        ref={headerRef}
        className="fixed top-0 right-0 left-0 z-50 border-b border-border bg-background/85 pt-[env(safe-area-inset-top)] shadow-[0_10px_28px_rgba(15,23,42,0.08)] backdrop-blur-md supports-[backdrop-filter]:bg-background/70 dark:border-glass-border-08 dark:bg-[#0b0b0f]/80 dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.1),0_10px_28px_rgba(0,0,0,0.22)] dark:supports-[backdrop-filter]:bg-[#0b0b0f]/60"
      >
        <div className="mx-auto flex h-14 max-w-[520px] items-center gap-3 px-4">
          <Button
            asChild
            variant="ghost"
            size="icon"
            className="h-9 w-9 shrink-0 rounded-full border-0 bg-transparent text-text-primary hover:bg-surface-alt dark:text-white/90 dark:hover:bg-glass-bg-08"
          >
            <Link
              href={backHref}
              replace
              aria-disabled={disabled}
              aria-label={backLabel}
              onClick={(event) => {
                if (!disabled && !interceptBack) return;
                event.preventDefault();
                onBack?.();
              }}
            >
              <ChevronLeft className="h-5 w-5" aria-hidden="true" />
            </Link>
          </Button>
          <h1 className="truncate text-lg leading-tight font-semibold text-text-primary dark:text-white">
            {title}
          </h1>
        </div>
      </header>

      <div className="h-14" />
    </>
  );
}
